// programmes.js — los nueve programas, desde el servidor de la radio.
//
// Contrato: `PROGRAMAS_WEB_INSTRUCCIONES.md` (servidor, 26.08.26).
// Endpoint: https://listen.astra.fm/programas.json · CORS abierto · `max-age=0, must-revalidate` (304 por ETag; medido 05.09.26).
//
// **Sustituye a WordPress como fuente.** Hasta ahora la parrilla salía de
// `getRadioDataFromWP()`, con dos problemas que este cambio resuelve solos:
//
//   · La web se caía con WordPress. Ahora la programación vive donde vive la
//     emisión.
//   · WordPress no tenía residente, así que esa columna de la parrilla iba
//     vacía. Aquí es `agente.nombre`.
//
// ⚠️ Tres trampas del contrato, todas fichadas:
//
//   1. **La clave NO es el nombre.** Se indexa por clave interna, y Nocturna
//      se llama `romanticos` por historia. Buscar por nombre visible falla en
//      silencio justo con ese programa.
//   2. **`range` no envuelve la medianoche.** Un programa de 22 a 00 llega como
//      `[22, 24]`, no `[22, 0]`, así que el rango siempre es creciente y aquí no
//      hay que deshacer ningún wrap.
//   3. **Una sola imagen por programa.** `imagenDestacada` sirve para los dos
//      temas —confirmado por Pablo, 26.08.26—, así que no hay pareja `-hi`/`-lo`
//      que buscar: la placa de señal la procesa el código.
import { getRadioCurrentTime, getRadioCurrentDay } from "@/utils/radioTime";
import { LISTEN } from "./host";

const BASE = `${LISTEN}`;
const URL_PROGRAMAS = `${BASE}/programas.json`;
const TTL = 5 * 60 * 1000;

let cache = null;
let cacheAt = 0;
let enVuelo = null;

/**
 * Olvida lo guardado y obliga a preguntar de nuevo.
 *
 * Hace falta porque la caché de arriba vive en el módulo: sobrevive a cambiar
 * de vista y solo caduca por tiempo. Sin esto, publicar algo y volver a la
 * pestaña no servía de nada — se seguía sirviendo lo aprendido hace cuatro
 * minutos.
 */
export function invalidar() {
  cache = null;
  cacheAt = 0;
}

/** Ruta relativa del contrato → URL absoluta. `null` si no hay imagen. */
function url(ruta) {
  return ruta ? BASE + ruta : "";
}

// Forma única para toda la web. Las vistas no deben conocer los nombres del
// contrato del servidor: si mañana cambia, se traduce aquí y solo aquí.
function normaliza(clave, p) {
  const range = Array.isArray(p.range) ? p.range : [0, 0];
  return {
    slug: clave,
    name: p.nombrePrograma || "",
    description: p.descripcion || "",
    range,
    startTime: range[0],
    endTime: range[1],
    days: p.dias || [],
    genres: p.generos || [],
    // ⚠️ **`genre` es solo el primero y ya casi no se usa**: se queda por
    // compatibilidad con lo que todavía lo lea. Para pintar, `generosDe()`.
    genre: (p.generos || [])[0] || "",
    resident: p.agente?.nombre || "",
    residentImage: url(p.agente?.imagen),
    image: url(p.imagenDestacada),
  };
}

/**
 * Los nueve, ya normalizados y ordenados por hora de inicio.
 * Ante un fallo de red devuelve el último caché que hubiera; si nunca hubo,
 * un array vacío. Nunca lanza: sin programación la web sigue sonando.
 */

/**
 * Los géneros de un programa, listos para pintarse: «Pop Electrónico, Electrónica».
 *
 * 🔴 **Un solo sitio donde se unen.** Encargo de Pablo, 21.09.26: «revisa que
 * se muestre así en todos los lugares, home, player, shows, detalles de show».
 * Antes cada vista lo resolvía a su manera —unas pintaban `genre`, que es solo
 * el primero, y las dos que los unían usaban punto medio—, así que el mismo
 * programa decía una cosa en la parrilla y otra en su ficha.
 *
 * ⚠️ **El separador es la coma**, que es como lo escribió Pablo y como se lee
 * una lista de cosas del mismo tipo. El punto medio de la casa junta cosas
 * distintas —«Revolución Urbana · 20–22»—, y mezclar los dos usos hacía que
 * «Post Punk · Dance Punk · Manchester» pareciera tres datos y no uno.
 *
 * ⚠️ Acepta las dos formas que circulan: `genres` de `programmes.js` y
 * `generos` de `currentShow.js` y `nextShow.js`.
 *
 * ⚠️ **Hay una excepción declarada y está escrita donde ocurre**: la cabecera de
 * móvil (`App.vue`) pinta solo el primero, porque su celda mide 142 y recorta
 * con puntos suspensivos; la lista entera diría menos que un género completo.
 */
export function generosDe(programa) {
  const lista = programa?.genres || programa?.generos || [];
  const todos = (Array.isArray(lista) ? lista : []).filter(Boolean);
  return todos.length ? todos.join(", ") : programa?.genre || "";
}

export async function getProgrammes() {
  const ahora = Date.now();
  if (cache && ahora - cacheAt < TTL) return cache;

  // Dos vistas montando a la vez piden lo mismo; se comparte la petición.
  if (enVuelo) return enVuelo;

  enVuelo = (async () => {
    try {
      const r = await fetch(URL_PROGRAMAS);
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      const lista = Object.entries(data.programs || {})
        .map(([clave, p]) => normaliza(clave, p))
        .sort((a, b) => a.startTime - b.startTime);
      if (lista.length) {
        cache = lista;
        cacheAt = ahora;
      }
      return cache || [];
    } catch (error) {
      // Red caída: mejor la programación de hace un rato que ninguna.
      return cache || [];
    } finally {
      enVuelo = null;
    }
  })();

  return enVuelo;
}

/** ¿Se emite hoy? Los nueve son diarios hoy, pero el contrato admite que no. */
function seEmiteHoy(programa, dia) {
  if (!programa.days.length) return true;
  return programa.days.includes(dia);
}

/** El programa en antena ahora mismo, o `null` si ninguno cubre esta hora. */
export async function getCurrentProgramme() {
  const lista = await getProgrammes();
  const hora = getRadioCurrentTime();
  const dia = getRadioCurrentDay();
  return (
    lista.find(
      (p) => seEmiteHoy(p, dia) && hora >= p.startTime && hora < p.endTime,
    ) || null
  );
}

/**
 * El siguiente. Si no queda ninguno hoy, el primero del día — que es lo que
 * suena tras la medianoche.
 */
export async function getNextProgramme() {
  const lista = await getProgrammes();
  const hora = getRadioCurrentTime();
  const hoy = lista.filter((p) => seEmiteHoy(p, getRadioCurrentDay()));
  if (!hoy.length) return null;
  return hoy.find((p) => p.startTime > hora) || hoy[0];
}

/** La parrilla del día, con el que está en antena marcado. */
export async function getTodayProgrammes() {
  const lista = await getProgrammes();
  const hora = getRadioCurrentTime();
  const dia = getRadioCurrentDay();
  return lista
    .filter((p) => seEmiteHoy(p, dia))
    .map((p) => ({
      ...p,
      start: p.startTime,
      end: p.endTime,
      // En minutos, que es lo que necesita la regla del día para repartir el
      // ancho de cada franja. Sin wrap que deshacer: ver trampa 2.
      startMin: Math.round(p.startTime * 60),
      endMin: Math.round(p.endTime * 60),
      live: hora >= p.startTime && hora < p.endTime,
    }));
}
